import { type ReactNode, useState } from "react";
import { useLocation } from "wouter";

import { CanvasPivotPanel } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { coverageRows } from "@/shared/userTaskCoverage.ts";
import type { UserTaskRule } from "@/types/userTasks.ts";
import { userTaskHref } from "@/types/userTaskLinks.ts";
import { useDeleteTaskRule, userTaskQueries, useUnmuteSender } from "./api.ts";
import { RowButton } from "./RowButton.tsx";
import { useQuery } from "@tanstack/react-query";

/** One standing entry, with the way to take it back. */
const SkipRow = ({
  text,
  action,
  ariaLabel,
  statsigName,
  disabled,
  onClick,
}: {
  text: string;
  action: string;
  ariaLabel: string;
  statsigName: string;
  disabled: boolean;
  onClick: () => void;
}) => (
  // The control sits beside what it acts on and never shrinks, so only the
  // words wrap. A sentence-long rule would otherwise push the button onto a
  // line of its own, where it reads as adrift from the rule it deletes.
  <li className="flex items-start justify-between gap-3">
    <span className="min-w-0 flex-1 break-words">{text}</span>
    <span className="shrink-0">
      <RowButton
        label={action}
        ariaLabel={ariaLabel}
        statsigName={statsigName}
        disabled={disabled}
        onClick={onClick}
      />
    </span>
  </li>
);

/**
 * One of the page's lists under its heading, in whichever of the three
 * states it is in.
 *
 * One component because the lists differ in their rows and in nothing else,
 * and a section that loaded, emptied or failed differently from its
 * neighbour would be two answers to one question. Each list has its own
 * state rather than the page having one: the sources are read from the task
 * list's own query and the two standing lists from their own, and one of
 * them failing should not take the others off the page.
 */
const ListSection = ({
  title,
  isPending,
  isError,
  onRetry,
  retryStatsigName,
  /** What the section says with nothing to list, which names how one gets added. */
  empty,
  isEmpty,
  intro,
  children,
}: {
  title: string;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  retryStatsigName: string;
  empty: string;
  isEmpty: boolean;
  intro?: string;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-3">
    <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</h2>
    {isPending && <Loader className="py-2" />}
    {!isPending && isError && (
      <div className="flex flex-col items-start gap-2 text-sm text-muted-foreground">
        Couldn't load this list.
        <Button variant="outline" size="sm" onClick={onRetry} data-statsig-name={retryStatsigName}>
          Try again
        </Button>
      </div>
    )}
    {!isPending && !isError && isEmpty && <p className="text-sm text-muted-foreground">{empty}</p>}
    {!isPending && !isError && !isEmpty && (
      <>
        {intro && <p className="text-sm text-muted-foreground">{intro}</p>}
        {children}
      </>
    )}
  </section>
);

/**
 * What okthx reads to make tasks, and what it has been told to do with it:
 * the sources and how their last read went, the senders it must not read,
 * and the reader's own rules about what counts as a task. At
 * `/tasks/sources-and-rules`, behind the toolbar's sliders.
 *
 * One page rather than a list each, because the three answer one question —
 * "where do these tasks come from, and why this one?" — and it is never
 * empty: a reader with no one muted and no rules still has the sources, which
 * is the first thing to check when a task is missing. The sources lead for
 * that reason. The native app's page is the same three sections in the same
 * order.
 */
export const SourcesAndRulesView = ({
  leading,
  accountSwitcher,
}: {
  leading?: ReactNode;
  accountSwitcher?: ReactNode;
}) => {
  const [, navigate] = useLocation();
  const list = useQuery(userTaskQueries.list());
  const mutedQuery = useQuery(userTaskQueries.mutedSenders());
  const rulesQuery = useQuery(userTaskQueries.rules());
  const unmute = useUnmuteSender({ failure: "Couldn't change it." });
  const deleteRule = useDeleteTaskRule();

  const sources = coverageRows(list.data?.coverage ?? []);
  const muted = mutedQuery.data?.mutedSenders ?? [];
  const rules = rulesQuery.data?.rules ?? [];
  // A rule is the user's own sentence and deleting it cannot be undone, where
  // unmuting only puts a sender back and can be redone from the next task.
  const [pendingDelete, setPendingDelete] = useState<UserTaskRule | null>(null);

  return (
    <>
      <CanvasPivotPanel
        title="Sources and rules"
        parentLabel="Tasks"
        onBack={() => navigate(userTaskHref())}
        backStatsigName="tasks-sources-back"
        leading={leading}
        accountSwitcher={accountSwitcher}
      >
        <div className="flex flex-col gap-7 text-sm">
          <ListSection
            title="Sources"
            isPending={list.isPending}
            isError={list.isError}
            onRetry={() => void list.refetch()}
            retryStatsigName="tasks-sources-retry"
            isEmpty={sources.length === 0}
            empty="Okthx hasn't read anything yet."
          >
            <ul className="flex flex-col gap-3">
              {sources.map((source) => (
                <li key={source.label} className="flex flex-col gap-0.5">
                  <span>{source.label}</span>
                  <span className="text-muted-foreground">{source.detail}</span>
                </li>
              ))}
            </ul>
          </ListSection>
          <ListSection
            title="Muted"
            isPending={mutedQuery.isPending}
            isError={mutedQuery.isError}
            onRetry={() => void mutedQuery.refetch()}
            retryStatsigName="tasks-muted-retry"
            isEmpty={muted.length === 0}
            empty="No one is muted. Open the ⋯ menu on a task to mute the sender it came from."
          >
            <ul className="flex flex-col gap-3">
              {muted.map((sender) => (
                <SkipRow
                  key={sender.value}
                  text={sender.value}
                  action="Unmute"
                  ariaLabel={`Unmute ${sender.value}`}
                  statsigName="tasks-unmute"
                  disabled={unmute.isPending}
                  onClick={() => unmute.mutate({ value: sender.value })}
                />
              ))}
            </ul>
          </ListSection>
          <ListSection
            title="Task rules"
            isPending={rulesQuery.isPending}
            isError={rulesQuery.isError}
            onRetry={() => void rulesQuery.refetch()}
            retryStatsigName="tasks-rules-retry"
            isEmpty={rules.length === 0}
            empty='No rules yet. Choose "Create a rule for this" in the ⋯ menu on a task, and Okthx writes one from your words.'
            // What the rules are, said once above them. They are not a block
            // list: each one is appended to okthx's own definition of a task
            // and takes precedence over it, so a rule can as easily ask for a
            // kind of task as rule one out.
            intro="These tell Okthx what counts as a task for you. They apply to new mail as it arrives, not to the tasks already on your list."
          >
            <ul className="flex flex-col gap-3">
              {rules.map((rule) => (
                <SkipRow
                  key={rule.id}
                  text={rule.text}
                  action="Delete"
                  ariaLabel="Delete rule"
                  statsigName="tasks-rule-delete"
                  disabled={deleteRule.isPending}
                  onClick={() => setPendingDelete(rule)}
                />
              ))}
            </ul>
          </ListSection>
        </div>
      </CanvasPivotPanel>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this rule?</AlertDialogTitle>
            <AlertDialogDescription>
              Okthx will go back to judging mail like this the way it did before.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) deleteRule.mutate({ ruleId: pendingDelete.id });
                setPendingDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
